import {useState, useEffect} from 'react'
import {Box, Typography} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import dayjs from 'dayjs'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import DatePickerNew from 'src/components-new/inputs/DatePickerNew'
import TimePickerNew from 'src/components-new/inputs/TimePickerNew'
import {useAppSelector} from 'src/hooks'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {mergeDateAndTime} from './agendaUtils'
import ParticipantLookupDialogNew from './ParticipantLookupDialogNew'

type OrganizeParticipant = {
  user: Record<string, any>
  participantUserGroup: 'PATIENT' | 'DOCTOR'
}

export interface VirtualMeetingOrganizeValues {
  title: string
  startDate: string
  endDate: string
  participants: OrganizeParticipant[]
}

interface VirtualMeetingOrganizeDialogNewProps {
  open: boolean
  participantUserGroup: string | string[]
  onClose: () => void
  onSave: (values: VirtualMeetingOrganizeValues) => void | Promise<void>
}

export default function VirtualMeetingOrganizeDialogNew({
  open,
  participantUserGroup,
  onClose,
  onSave,
}: VirtualMeetingOrganizeDialogNewProps): JSX.Element {
  const [title, setTitle] = useState('')
  const [startDay, setStartDay] = useState<string | null>(null)
  const [startTime, setStartTime] = useState<string | null>(null)
  const [endTime, setEndTime] = useState<string | null>(null)
  const [participants, setParticipants] = useState<OrganizeParticipant[]>([])
  const [lookupMode, setLookupMode] = useState<'patient' | 'doctor' | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const userInfoData = useAppSelector(({userReducer}) => userReducer.data)

  useEffect(() => {
    if (!open) {
      setTitle('')
      setStartDay(null)
      setStartTime(null)
      setEndTime(null)
      setParticipants([])
      setSubmitted(false)
      setSaving(false)
    }
  }, [open])

  const groups = Array.isArray(participantUserGroup) ? participantUserGroup : [participantUserGroup]
  const canAddPatient = groups.includes('PATIENT')
  const canAddDoctor = groups.includes('DOCTOR')

  const startDayInPast = Boolean(startDay) && dayjs(startDay).isBefore(dayjs().startOf('day'))
  const combinedStart = startDay && startTime ? mergeDateAndTime(startDay, startTime) : null
  const startTimeInPast = !startDayInPast && Boolean(combinedStart) && dayjs(combinedStart).isBefore(dayjs())
  const endTimeError =
    submitted && startTime && endTime ? !dayjs(endTime).isAfter(dayjs(startTime)) : false

  const handleAddParticipant = (row: Record<string, any>, group: 'PATIENT' | 'DOCTOR') => {
    setParticipants(prev => (prev.some(p => p.user._id === row._id) ? prev : [...prev, {user: row, participantUserGroup: group}]))
    setLookupMode(null)
  }

  const handleRemoveParticipant = (userId: string) => {
    setParticipants(prev => prev.filter(p => p.user._id !== userId))
  }

  const handleSubmit = async () => {
    if (saving) return
    setSubmitted(true)
    if (!title.trim() || !startDay || !startTime || !endTime || participants.length === 0) return
    if (startDayInPast || startTimeInPast) return

    const startISO = mergeDateAndTime(startDay, startTime)
    const endISO = mergeDateAndTime(startDay, endTime)
    if (!startISO || !endISO || !dayjs(endISO).isAfter(dayjs(startISO))) return

    setSaving(true)
    try {
      await onSave({
        title,
        startDate: startISO,
        endDate: endISO,
        participants: participants.map(p => ({user: p.user, participantUserGroup: p.participantUserGroup})),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <AppDialog
        actions={
          <>
            <Button disabled={saving} onClick={onClose} variant="secondary">Annulla</Button>
            <Button loading={saving} onClick={handleSubmit} variant="primary">Salva</Button>
          </>
        }
        maxWidth="md"
        onClose={onClose}
        open={open}
        title="Organizza incontro virtuale"
      >
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 2, pt: 1}}>
          <TextFieldNew
            error={submitted && !title.trim()}
            helperText={submitted && !title.trim() ? 'Campo obbligatorio' : undefined}
            label="Titolo"
            onChange={setTitle}
            required
            sx={{gridColumn: '1 / -1'}}
            value={title}
          />
          <DatePickerNew
            error={(submitted && !startDay) || startDayInPast}
            helperText={
              submitted && !startDay
                ? 'Campo obbligatorio'
                : startDayInPast
                  ? "L'appuntamento non può essere nel passato"
                  : undefined
            }
            label="Data evento"
            onChange={setStartDay}
            required
            sx={{gridColumn: '1 / -1'}}
            value={startDay}
          />
          <TimePickerNew
            error={(submitted && !startTime) || (submitted && startTimeInPast)}
            helperText={
              submitted && !startTime
                ? 'Campo obbligatorio'
                : submitted && startTimeInPast
                  ? "L'orario non può essere nel passato"
                  : undefined
            }
            label="Dalle"
            onChange={setStartTime}
            required
            value={startTime}
          />
          <TimePickerNew
            error={(submitted && !endTime) || Boolean(endTimeError)}
            helperText={
              submitted && !endTime
                ? 'Campo obbligatorio'
                : endTimeError
                  ? "L'orario di fine deve essere successivo all'orario di inizio"
                  : undefined
            }
            label="Alle"
            onChange={setEndTime}
            required
            value={endTime}
          />

          <Box sx={{gridColumn: '1 / -1'}}>
            <Typography
              sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}
              variant="caption"
            >
              Partecipanti
            </Typography>
            <Box sx={{display: 'flex', gap: 1, mt: 0.5, mb: 1}}>
              {canAddPatient && (
                <Button onClick={() => setLookupMode('patient')} startIcon={<SearchIcon fontSize="small" />} variant="ghost">
                  Aggiungi Paziente
                </Button>
              )}
              {canAddDoctor && (
                <Button onClick={() => setLookupMode('doctor')} startIcon={<SearchIcon fontSize="small" />} variant="ghost">
                  Aggiungi Medico
                </Button>
              )}
            </Box>
            {submitted && participants.length === 0 && (
              <Typography sx={{color: 'error.main', display: 'block', mb: 1}} variant="caption">
                Aggiungere almeno un partecipante
              </Typography>
            )}
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
              {participants.map(p => (
                <Box
                  key={p.user._id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 0.75,
                    borderRadius: '8px',
                    backgroundColor: NEUTRAL_SCALE[50],
                    border: `1px solid ${NEUTRAL_SCALE[200]}`,
                  }}
                >
                  <Typography variant="body2">
                    {p.user.firstName} {p.user.lastName}
                    {p.user.email ? ` - ${p.user.email}` : ''}
                    {` (${p.participantUserGroup === 'PATIENT' ? 'Paziente' : 'Medico'})`}
                  </Typography>
                  <ActionIconButton onClick={() => handleRemoveParticipant(p.user._id)} tooltip="Rimuovi" variant="close" />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </AppDialog>

      {lookupMode && (
        <ParticipantLookupDialogNew
          customFilters={lookupMode === 'doctor' ? [{property: 'code', value: `!${userInfoData?.code}`}] : undefined}
          mode={lookupMode}
          onClose={() => setLookupMode(null)}
          onRowClick={(row) => handleAddParticipant(row, lookupMode === 'patient' ? 'PATIENT' : 'DOCTOR')}
        />
      )}
    </>
  )
}
